import React, { useEffect, useMemo, useRef } from 'react';

import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  OutlinedInput,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Formik } from 'formik';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import DialogTransition from 'src/components/DialogTransition';
import theme from 'src/theme';
import PercentageInputMask from 'src/components/inputMask/PercentageInputMask';
import validationSchema from './schema';
import useStyles from '../../styles';
import makeInitialState from './initialState';

const MsiDiscountForm = ({ open, data, onClose, onCreate, onEdit }) => {
  const { classes } = useStyles();
  const resetRef = useRef();
  const { t } = useTranslation();
  const initialState = useMemo(() => makeInitialState(data), [data]);

  const handleOuterSubmit = useMemo(() => {
    if (data) {
      return onEdit;
    }
    return onCreate;
  }, [data, onEdit, onCreate]);

  useEffect(() => {
    if (resetRef.current) {
      resetRef.current();
    }
  }, [open, resetRef, onCreate]);

  return (
    <div>
      <Dialog
        classes={{ paper: classes.root }}
        open={open}
        scroll="body"
        TransitionComponent={DialogTransition}
        keepMounted
        disableEscapeKeyDown
      >
        <DialogTitle className={classes.title} key="dialog-title">
          <IconButton
            key="close"
            aria-label="Close"
            color="inherit"
            onClick={onClose}
            className={classes.closeIcon}
          >
            <CloseIcon />
          </IconButton>
          <Typography className={classes.title} color="primary" variant="h2">
            {data
              ? t('CATALOGS_VIEW.MSI_DISCOUNT.UPDATE_MSI_DISCOUNT')
              : t('CATALOGS_VIEW.MSI_DISCOUNT.ADD_MSI_DISCOUNT')}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Formik
            initialValues={initialState}
            enableReinitialize
            validationSchema={validationSchema}
            onSubmit={handleOuterSubmit}
          >
            {({
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              touched,
              values,
              resetForm
            }) => {
              resetRef.current = () => resetForm(initialState);
              return (
                <form onSubmit={handleSubmit}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel
                      htmlFor="name-label"
                      error={Boolean(touched.name && errors.name)}
                    >
                      {t('CATALOGS_VIEW.MSI_DISCOUNT.RATE_NAME')}
                    </InputLabel>
                    <OutlinedInput
                      id="name-label"
                      name="name"
                      placeholder={t(
                        'CATALOGS_VIEW.MSI_DISCOUNT.ENTER_RATE_NAME'
                      )}
                      label={t('CATALOGS_VIEW.MSI_DISCOUNT.RATE_NAME')}
                      value={values.name}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      error={Boolean(touched.name && errors.name)}
                    />
                    {Boolean(touched.name && errors.name) && (
                      <FormHelperText sx={{ color: theme.palette.error.main }}>
                        {t(errors.name)}
                      </FormHelperText>
                    )}
                  </FormControl>
                  <FormControl fullWidth margin="normal">
                    <InputLabel
                      htmlFor="implicitRate-label"
                      error={Boolean(
                        touched.implicitRate && errors.implicitRate
                      )}
                    >
                      {t('CATALOGS_VIEW.MSI_DISCOUNT.IMPLICIT_RATE')}
                    </InputLabel>
                    <OutlinedInput
                      id="implicitRate-label"
                      name="implicitRate"
                      placeholder="0%"
                      label={t('CATALOGS_VIEW.MSI_DISCOUNT.IMPLICIT_RATE')}
                      value={values.implicitRate}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      inputComponent={PercentageInputMask}
                      error={Boolean(
                        touched.implicitRate && errors.implicitRate
                      )}
                    />
                    {Boolean(touched.implicitRate && errors.implicitRate) && (
                      <FormHelperText sx={{ color: theme.palette.error.main }}>
                        {t(errors.implicitRate)}
                      </FormHelperText>
                    )}
                  </FormControl>
                  <FormControl fullWidth margin="normal">
                    <InputLabel
                      htmlFor="insurance-label"
                      error={Boolean(touched.insurance && errors.insurance)}
                    >
                      {t('CATALOGS_VIEW.MSI_DISCOUNT.INSURANCE')}
                    </InputLabel>
                    <OutlinedInput
                      id="insurance-label"
                      name="insurance"
                      placeholder="0%"
                      label={t('CATALOGS_VIEW.MSI_DISCOUNT.INSURANCE')}
                      value={values.insurance}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      inputComponent={PercentageInputMask}
                      error={Boolean(touched.insurance && errors.insurance)}
                    />
                    {Boolean(touched.insurance && errors.insurance) && (
                      <FormHelperText sx={{ color: theme.palette.error.main }}>
                        {t(errors.insurance)}
                      </FormHelperText>
                    )}
                  </FormControl>
                  <Grid container direction="column" alignItems="stretch">
                    <Grid item>
                      <Button
                        className={clsx(classes.buttons, classes.label)}
                        type="submit"
                        color="primary"
                        variant="outlined"
                      >
                        {data ? t('COMMON.UPDATE') : t('COMMON.CREATE')}
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        className={clsx(classes.buttons, classes.outlined)}
                        onClick={onClose}
                        color="primary"
                        variant="outlined"
                      >
                        {t('COMMON.CANCEL')}
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              );
            }}
          </Formik>
        </DialogContent>
      </Dialog>
    </div>
  );
};

MsiDiscountForm.propTypes = {
  open: PropTypes.bool.isRequired,
  data: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired
};

export default MsiDiscountForm;
